import React, { useState, useEffect } from 'react';
import { useStoreInfo } from '../../hooks/useApi';
import './Hero.css';
import axios from 'axios';
import { apiService } from '../../services/api';

const Hero: React.FC = () => {
  const [itemImages, setItemImages] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const { data: storeInfo, loading, error } = useStoreInfo();

  useEffect(() => {
    // Load item images from the public/images/items directory
    const loadItemImages = async () => {
      try {
        const imagePaths: string[] = await apiService.getGallery(); 
        setItemImages(imagePaths);
      } catch (error) {
        console.log('No custom item images found, using placeholder');
      }
    };

    loadItemImages();
  }, []);

  // Cycle through gallery images every few seconds (pausable)
  useEffect(() => {
    if (!itemImages || itemImages.length === 0) {
      setCurrentIndex(0);
      return;
    }

    // Reset to first image when gallery updates
    setCurrentIndex(0);

    if (isPaused) return; // do not start interval when paused

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (itemImages.length ? (prev + 1) % itemImages.length : 0));
    }, 3000); // change image every 3 seconds

    return () => clearInterval(interval);
  }, [itemImages, isPaused]);

  // Show loading state
  if (loading) {
    return (
      <section id="home" className="hero">
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="skeleton-line long" style={{ height: '60px', marginBottom: '1rem' }}></div>
              <div className="skeleton-line medium" style={{ height: '40px', marginBottom: '1rem' }}></div>
              <div className="skeleton-line long" style={{ height: '24px', marginBottom: '2rem' }}></div>
              <div className="skeleton-line short" style={{ height: '40px', width: '120px' }}></div>
            </div>
            <div className="hero-image">
              <div className="skeleton-line" style={{ height: '400px', width: '300px', borderRadius: '12px' }}></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error || !storeInfo) {
    return (
      <section id="home" className="hero">
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                חיי-קדושה
                <span className="hero-title-accent">כל מה שצריך כדי לחיות חיים קדושים</span>
              </h1>
              <p className="hero-description">
                טליתות ,תפילין ,כיסויים וכלל תשמישי הקדושה במחירים נוחים ואיכות הכי גבוהה בשוק
              </p>
              <div className="hero-buttons">
                <a href="#items" className="btn btn-primary">
                  המוצרים שלנו
                </a>
                <a href="#about" className="btn">
                  עלינו
                </a>
              </div>
            </div>
            <div className="hero-image">
              <div className="hero-item-stack">
                <div className="item item-1">
                  <span className="item-icon">✨</span>
                </div>
                <div className="item item-2">
                  <span className="item-icon">🕊️</span>
                </div>
                <div className="item item-3">
                  <span className="item-icon">⛪</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="home" className="hero">
      <div className="hero-background">
        <div className="hero-overlay"></div>
      </div>
      <div className="container">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
            {storeInfo.name}
              <span className="hero-title-accent">כל מה שצריך כדי לחיות חיים קדושים</span>
            </h1>
            <p className="hero-description">
              {storeInfo.description}
            </p>
            <div className="hero-buttons">
              <a href="#items" className="btn btn-primary">
                המוצרים שלנו
              </a>
              <a href="#about" className="btn">
                עלינו
              </a>
            </div>
          </div>
          <div className="hero-image">
            <div className="hero-item-stack">
              {itemImages.length > 0 ? (
                <div
                  className="hero-gallery"
                  onMouseEnter={() => setIsPaused(true)}
                  onMouseLeave={() => setIsPaused(false)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowLeft') {
                      setCurrentIndex((prev) => (itemImages.length ? (prev - 1 + itemImages.length) % itemImages.length : 0));
                    } else if (e.key === 'ArrowRight') {
                      setCurrentIndex((prev) => (itemImages.length ? (prev + 1) % itemImages.length : 0));
                    }
                  }}
                  aria-label="Gallery"
                >
                  <img
                    src={itemImages[currentIndex]}
                    alt={`פריט ${currentIndex + 1}`}
                    className="hero-gallery-image"
                    loading="lazy"
                    onError={(e) => {
                      // Fallback to placeholder if image fails to load
                      const target = e.currentTarget as HTMLImageElement;
                      target.onerror = null;
                      target.src = '/static/img-placeholder.svg';
                    }}
                  />

                  {/* elegant indicators (dots) — user can click to jump to an image */}
                  <div className="gallery-indicators" role="tablist" aria-label="Image indicators">
                    {itemImages.map((_, idx) => (
                      <button
                        key={idx}
                        className={`gallery-dot ${idx === currentIndex ? 'active' : ''}`}
                        onClick={() => setCurrentIndex(idx)}
                        aria-label={`הצג תמונה ${idx + 1}`}
                        aria-pressed={idx === currentIndex}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div className="item item-1">
                    <span className="item-icon">✨</span>
                  </div>
                  <div className="item item-2">
                    <span className="item-icon">🕊️</span>
                  </div>
                  <div className="item item-3">
                    <span className="item-icon">⛪</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;

